import { matchPath } from 'react-router-dom';
import { RequestParameters, UploadableMap, Variables } from 'relay-runtime';
import config from 'src/shared/config';
import routes from 'src/shared/routes';
import { getHeaders, getRequestBody, handleData, isMutation } from './helpers';

export const GRAPHQL_URL = config.graphqlUrl || config.apiUrl + 'graphql';

// Check if current route allows guest access, if not redirect to login
export const shouldThrowErrorForRedirect = (): boolean => {
    const pathName = window.location.pathname;
    const currentRoute = routes.find((x) => {
        const matchWithParams = matchPath(x.url.replaceAll('?', ''), pathName);
        const urlWithNoOptionalParams = x.url.replace(/:w+\?/g, '');
        const matchNoParams = matchPath(urlWithNoOptionalParams, pathName + '/');
        return matchNoParams || matchWithParams;
    });

    return !currentRoute?.isGuestAllowed;
};

// Define a function that fetches the results of a request (query/mutation/etc)
// and returns its results as a Promise:
const fetchQuery = async (request: RequestParameters, variables: Variables, uploadables?: UploadableMap | null) => {
    try {
        const body = getRequestBody(request, variables, uploadables);
        const headers = {
            ...getHeaders(uploadables)
        } as unknown as Headers;

        const fetchRequest = await fetch(GRAPHQL_URL, {
            method: 'POST',
            headers,
            body
        });

        if (fetchRequest.status === 401) {
            if (shouldThrowErrorForRedirect()) {
                throw new Error('Unauthenticated seller');
            }
        }

        const data = await handleData(fetchRequest);
        validateAuthentication(data);

        const invalidPrintfulRefreshTokenError = data.errors?.find(
            (error: any) =>
                typeof error.extensions?.message === 'string' &&
                (error.extensions.message.includes('PrintfulCommonProductProvider.RefreshToken') ||
                    error.extensions.message.includes('Refresh token is not valid'))
        );

        if (invalidPrintfulRefreshTokenError) {
            throw new Error(invalidPrintfulRefreshTokenError.extensions.message);
        }

        if (isMutation(request) && data.errors && shouldThrowErrorForRedirect()) {
            throw data;
        }

        if (!data.data && shouldThrowErrorForRedirect()) {
            throw data.errors;
        }

        return data ?? {};
    } catch (err) {
        // eslint-disable-next-line
        console.error('err: ', err);

        throw err;
    }
};

// Validate autentication
const validateAuthentication = (data: any) => {
    const checkExtensions = (obj: any) => {
        if (obj && obj.errors && Array.isArray(obj.errors)) {
            for (const err of obj.errors) {
                if (err.message && (err.message.includes('Unauthenticated seller') || err.message.includes('Unauthorized'))) {
                    if (shouldThrowErrorForRedirect()) {
                        throw new Error('Unauthenticated seller');
                    }
                }
            }
        }

        if (
            obj &&
            obj.extensions &&
            obj.extensions.message &&
            (obj.extensions.message.includes('Unauthenticated seller') || obj.extensions.message.includes('Unauthorized'))
        ) {
            if (shouldThrowErrorForRedirect()) {
                throw new Error('Unauthenticated seller');
            }
        }

        if (typeof obj === 'object') {
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    checkExtensions(obj[key]);
                }
            }
        }
    };

    checkExtensions(data);
};

export default fetchQuery;
